import { ModalCloseButton } from '@components/lib/modal/modal';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TIntegrationType } from '@gen2/api/integrations/api';
import {
  useWorkatoEmbeddedLinkQuery,
  useWorkatoManagedConnectionQuery,
} from '@gen2/api/integrations/hooks';
import { usePermissions } from '@gen2/utils/permissions/permissions';
import { Loader } from '@nx-fe/components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  StyledContainer,
  StyledEmptyBox,
  StyledIntDetails,
  StyledIntDetailsBody,
  StyledIntDetailsHeader,
  StyledIntDetailsHeaderLeft,
  StyledIntDetailsHeaderLeftImg,
  StyledIntDetailsHeaderRight,
  StyledIntDetailsHeaderRightH3,
  StyledIntDetailsHeaderRightLink,
  StyledIntDetailsHeaderRightP,
} from './integration.styled';

const ManagedIntegration = ({ type }: { type?: TIntegrationType }) => {
  const { t } = useTranslation('integrations');
  const navigate = useNavigate();
  const {
    data: connectionData,
    isLoading: isConnectionLoading,
    isError: isConnectionError,
  } = useWorkatoManagedConnectionQuery(type as TIntegrationType);

  const shouldFetchEmbeddedLink =
    !isConnectionLoading &&
    !isConnectionError &&
    connectionData?.data.connections.length > 0;

  const { data: embeddedLinkData, isLoading: isLinkLoading } =
    useWorkatoEmbeddedLinkQuery(type as TIntegrationType, {
      enabled: shouldFetchEmbeddedLink,
    });

  // Show loader while waiting for connection data
  if (isConnectionLoading) {
    return (
      <StyledContainer>
        <StyledIntDetails>
          <ModalCloseButton
            data-cy={`integration-${type}-close`}
            aria-label="close"
            onClick={() => navigate('/integrations')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ModalCloseButton>
          <StyledIntDetailsHeader>
            <StyledIntDetailsHeaderLeft>
              <StyledIntDetailsHeaderLeftImg
                src={`/assets/int-${type}.png`}
                alt={t(`${type}.thumbnail.title`) || ''}
              />
            </StyledIntDetailsHeaderLeft>
            <StyledIntDetailsHeaderRight>
              <StyledIntDetailsHeaderRightH3>
                {t(`${type}.thumbnail.title`)}
              </StyledIntDetailsHeaderRightH3>
              <StyledIntDetailsHeaderRightP>
                {t(`${type}.thumbnail.desc`)}
              </StyledIntDetailsHeaderRightP>
              <StyledIntDetailsHeaderRightLink
                href={t(`${type}.url`) || '#'}
                target="_blank"
              >
                {t(`${type}.link`)}
              </StyledIntDetailsHeaderRightLink>
            </StyledIntDetailsHeaderRight>
          </StyledIntDetailsHeader>
          <StyledIntDetailsBody
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Loader />
          </StyledIntDetailsBody>
        </StyledIntDetails>
      </StyledContainer>
    );
  }

  const showContactSupportModal = !connectionData?.data.connections.length;

  if (showContactSupportModal) {
    return (
      <StyledContainer>
        <StyledIntDetails>
          <ModalCloseButton
            data-cy={`integration-${type}-close`}
            aria-label="close"
            onClick={() => navigate('/integrations')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ModalCloseButton>
          <StyledIntDetailsHeader>
            <StyledIntDetailsHeaderLeft>
              <StyledIntDetailsHeaderLeftImg
                src={`/assets/int-${type}.png`}
                alt={t(`${type}.thumbnail.title`) || ''}
              />
            </StyledIntDetailsHeaderLeft>
            <StyledIntDetailsHeaderRight>
              <StyledIntDetailsHeaderRightH3>
                {t(`${type}.thumbnail.title`)}
              </StyledIntDetailsHeaderRightH3>
              <StyledIntDetailsHeaderRightP>
                {t(`${type}.thumbnail.desc`)}
              </StyledIntDetailsHeaderRightP>
              <StyledIntDetailsHeaderRightLink
                href={t(`${type}.url`) || '#'}
                target="_blank"
              >
                {t(`${type}.link`)}
              </StyledIntDetailsHeaderRightLink>
            </StyledIntDetailsHeaderRight>
          </StyledIntDetailsHeader>
          <StyledIntDetailsBody>
            <StyledEmptyBox>
              <h3>{t('managed.empty.title')}</h3>
              <p>{t('managed.empty.description')}</p>
            </StyledEmptyBox>
          </StyledIntDetailsBody>
        </StyledIntDetails>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <StyledIntDetails>
        <ModalCloseButton
          data-cy={`integration-${type}-close`}
          aria-label="close"
          onClick={() => navigate('/integrations')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </ModalCloseButton>
        <StyledIntDetailsHeader>
          <StyledIntDetailsHeaderLeft>
            <StyledIntDetailsHeaderLeftImg
              src={`/assets/int-${type}.png`}
              alt={t(`${type}.thumbnail.title`) || ''}
            />
          </StyledIntDetailsHeaderLeft>
          <StyledIntDetailsHeaderRight>
            <StyledIntDetailsHeaderRightH3>
              {t(`${type}.thumbnail.title`)}
            </StyledIntDetailsHeaderRightH3>
            <StyledIntDetailsHeaderRightP>
              {t(`${type}.thumbnail.desc`)}
            </StyledIntDetailsHeaderRightP>
            <StyledIntDetailsHeaderRightLink
              href={t(`${type}.url`) || '#'}
              target="_blank"
            >
              {t(`${type}.link`)}
            </StyledIntDetailsHeaderRightLink>
          </StyledIntDetailsHeaderRight>
        </StyledIntDetailsHeader>
        <StyledIntDetailsBody>
          {embeddedLinkData?.data.link && !isLinkLoading ? (
            <iframe
              width="100%"
              title={`${type}-integration-iframe`}
              style={{
                border: 'none',
                minHeight: '500px',
                height: '640px',
                overflow: 'hidden',
                display: 'block',
              }}
              src={embeddedLinkData?.data.link}
            />
          ) : (
            <Loader />
          )}
        </StyledIntDetailsBody>
      </StyledIntDetails>
    </StyledContainer>
  );
};

export const ManagedIntegrationWrapper = () => {
  const { withPermissions } = usePermissions();
  const { type } = useParams<{ type: TIntegrationType }>();

  return <ManagedIntegration type={type} />;
};

export default ManagedIntegration;
